import { useCallback, useEffect, useRef, useState } from 'react';

import { trackButtonClick } from '../services/telemetry';

/**
 * Distinguishes auto-saves triggered by the edit-mode "Done" button
 * from explicit user saves so analytics can split them. Threaded
 * through to the consumer's `save` so both this hook and any future
 * "Save" affordance can tag the same `Save Changes` event.
 */
export type EditSaveSource = 'edit-done' | 'manual';

interface UseProjectEditSessionOptions<TSnapshot, TUndo = TSnapshot> {
  /**
   * Take a snapshot of the current fingering state so it can be
   * restored later (by Reset / Undo). Return `null` to abort
   * entering edit mode — e.g. when the underlying data hasn't
   * finished loading and there's nothing to snapshot.
   */
  getSnapshot: () => TSnapshot | null;
  /**
   * Restore the given snapshot — repaint the score and push the
   * restored state through any sibling state-queue / overlay
   * pipeline the caller maintains. The hook never mutates the
   * snapshot itself, so it's the consumer's call whether to clone
   * defensively before mutating.
   */
  applySnapshot: (snapshot: TSnapshot) => void;
  /**
   * Deep-clone a snapshot so Reset can hand the baseline back to
   * `applySnapshot` without losing the canonical original. Defaults
   * to a `structuredClone` wrapper — override only if your snapshot
   * contains types `structuredClone` can't handle.
   */
  cloneSnapshot?: (snapshot: TSnapshot) => TSnapshot;
  /**
   * Persist the current state when the user presses "Done". Must
   * return `true` on success. Done only exits edit mode + tears
   * down the baseline/undo refs on success; on failure the user
   * stays in edit mode so they can retry without losing their
   * pending edits. The `source` argument lets the caller's
   * telemetry distinguish auto-saves from manual saves.
   */
  save: (source: EditSaveSource) => Promise<boolean>;
  /**
   * When set, Undo stores the value passed to `pushEditUndo` and hands
   * it here instead of replacing the score with a full snapshot. Reset
   * still restores the entry-time baseline through `applySnapshot`.
   */
  applyUndo?: (entry: TUndo) => void;
  /**
   * Telemetry page name (e.g. `'PdfProjectPage'` or `'ProjectPage'`)
   * used for every edit-mode `trackButtonClick` call this hook
   * makes (`Enter Edit Mode`, `Edit Undo`, `Edit Reset`, `Edit Done`).
   * Kept as a constrained string union so typos are caught at the
   * call site — extend the union if a third project surface adds an
   * edit mode.
   */
  telemetrySource: 'PdfProjectPage' | 'ProjectPage';
}

export interface UseProjectEditSessionResult<TSnapshot, TUndo = TSnapshot> {
  isEditMode: boolean;
  /** Mirror of `isEditMode` for use inside non-React callbacks
   *  (`addEventListener` closures, RAF callbacks, etc.) that need
   *  the latest value without re-binding when state flips. */
  isEditModeRef: React.RefObject<boolean>;
  /** Undo-stack depth, exposed so the toolbar's Reset/Undo can
   *  toggle their `disabled` state without reading a ref in JSX. */
  editUndoCount: number;
  /** Push the given snapshot onto the in-session undo stack so the
   *  next call to `editUndo` restores it. No-op when not in edit
   *  mode — keeps the gate consistent so callers (drag handlers,
   *  swap handlers, etc.) can call `pushEditUndo` unconditionally
   *  before every mutation. */
  pushEditUndo: (snapshot: TUndo) => void;
  enterEditMode: () => void;
  editUndo: () => void;
  editReset: () => void;
  editDone: () => Promise<void>;
  /** Pencil-pill handler — flips edit mode on (Enter) or off
   *  (Done + save), so the pencil button doesn't have to thread its
   *  own conditional in the consumer. */
  togglePencil: () => Promise<void>;
}

/**
 * Owns the "edit a project's fingerings" session state — the
 * isEditMode flag, the entry-time baseline, the in-session undo
 * stack, and the Enter / Undo / Reset / Done / Pencil handlers.
 *
 * Mirrors the Mobile WebView semantics in
 * `MobileApp/components/project/fingeringWebViewHtml.ts` and the
 * `useProjectEditState` hook in `MobileApp/hooks/`:
 *   - `getSnapshot` is called once on Enter to take the baseline.
 *   - Drag / swap handlers call `pushEditUndo(currentSnapshot)`
 *     BEFORE each mutation. The stack stores pre-mutation states.
 *   - Undo pops the most recent pre-mutation snapshot and applies
 *     it. Reset clears the stack and applies the entry-time baseline.
 *   - Done saves; on success it exits edit mode and tears down
 *     baseline + stack. On failure it stays in edit mode so the
 *     user can retry without losing pending edits.
 *
 * Generic over `TSnapshot` so the PDF page (which stores
 * `ScoreSystemData[]`) and the XML page (which stores
 * `FingeringData`) can share a single implementation. The hook
 * never reads or mutates the snapshot shape — it only stores,
 * clones (via `cloneSnapshot`), and hands it back through
 * `applySnapshot`, leaving payload-specific concerns to the caller.
 *
 * `getSnapshot` / `applySnapshot` / `cloneSnapshot` / `save` can be
 * inline closures (commonly the case); the hook captures the
 * latest versions through a ref so the returned handlers don't
 * have to be re-created on every render of the consumer.
 */
// Default deep-clone wrapper. Firefox throws "structuredClone called
// on an object that does not implement interface Window" if the
// function is referenced bare (e.g. as a default parameter that
// later gets destructured / re-assigned), because it expects to be
// called with `window` as the receiver. Wrapping it in an arrow
// pins the call to the global so the default works in every
// engine — Chrome, Firefox, Safari.
const defaultCloneSnapshot = <T>(snapshot: T): T => structuredClone(snapshot);

export function useProjectEditSession<TSnapshot, TUndo = TSnapshot>({
  getSnapshot,
  applySnapshot,
  cloneSnapshot = defaultCloneSnapshot,
  save,
  applyUndo,
  telemetrySource,
}: UseProjectEditSessionOptions<TSnapshot, TUndo>): UseProjectEditSessionResult<
  TSnapshot,
  TUndo
> {
  const [isEditMode, setIsEditMode] = useState(false);
  const isEditModeRef = useRef(false);
  useEffect(() => {
    isEditModeRef.current = isEditMode;
  }, [isEditMode]);

  const baselineRef = useRef<TSnapshot | null>(null);
  const undoStackRef = useRef<TUndo[]>([]);
  // Re-render trigger for the toolbar's Reset / Undo `disabled`
  // state — we don't drive JSX off `undoStackRef.current.length`
  // directly because mutations to the ref don't re-render.
  const [editUndoCount, setEditUndoCount] = useState(0);

  // Capture the latest closures in refs so the public handlers
  // don't re-create on every render (which would invalidate the
  // memoised callbacks that consumers pass into child components).
  const optionsRef = useRef({
    getSnapshot,
    applySnapshot,
    cloneSnapshot,
    save,
    applyUndo,
  });
  useEffect(() => {
    optionsRef.current = {
      getSnapshot,
      applySnapshot,
      cloneSnapshot,
      save,
      applyUndo,
    };
  });

  const pushEditUndo = useCallback((snapshot: TUndo) => {
    if (!isEditModeRef.current) return;
    undoStackRef.current.push(snapshot);
    setEditUndoCount(undoStackRef.current.length);
  }, []);

  const enterEditMode = useCallback(() => {
    const snapshot = optionsRef.current.getSnapshot();
    if (snapshot === null) return;
    baselineRef.current = optionsRef.current.cloneSnapshot(snapshot);
    undoStackRef.current = [];
    setEditUndoCount(0);
    setIsEditMode(true);
    trackButtonClick(telemetrySource, 'Enter Edit Mode');
  }, [telemetrySource]);

  const editUndo = useCallback(() => {
    if (undoStackRef.current.length === 0) return;
    const previous = undoStackRef.current.pop()!;
    setEditUndoCount(undoStackRef.current.length);
    if (optionsRef.current.applyUndo) {
      optionsRef.current.applyUndo(previous);
    } else {
      optionsRef.current.applySnapshot(previous as unknown as TSnapshot);
    }
    trackButtonClick(telemetrySource, 'Edit Undo');
  }, [telemetrySource]);

  const editReset = useCallback(() => {
    if (!baselineRef.current) return;
    // Clone defensively before applying so a consumer that mutates
    // its snapshot in place can't corrupt the canonical baseline.
    const baseline = optionsRef.current.cloneSnapshot(baselineRef.current);
    undoStackRef.current = [];
    setEditUndoCount(0);
    optionsRef.current.applySnapshot(baseline);
    trackButtonClick(telemetrySource, 'Edit Reset');
  }, [telemetrySource]);

  const editDone = useCallback(async () => {
    const ok = await optionsRef.current.save('edit-done');
    if (!ok) return;
    baselineRef.current = null;
    undoStackRef.current = [];
    setEditUndoCount(0);
    setIsEditMode(false);
    trackButtonClick(telemetrySource, 'Edit Done');
  }, [telemetrySource]);

  const togglePencil = useCallback(async () => {
    if (isEditModeRef.current) {
      await editDone();
    } else {
      enterEditMode();
    }
  }, [editDone, enterEditMode]);

  return {
    isEditMode,
    isEditModeRef,
    editUndoCount,
    pushEditUndo,
    enterEditMode,
    editUndo,
    editReset,
    editDone,
    togglePencil,
  };
}
